/* eslint-disable @typescript-eslint/no-empty-function */
import { Location } from '@angular/common';
import { Component, HostListener, Inject, ChangeDetectionStrategy } from '@angular/core';
import {
  FacadeInterface,
  FormValueInterface,
  PERMISSION_CREATE,
  PERMISSION_READ,
} from '@ellf/app-common';
import { RouterService } from '@ellf/app-routing';
import { BaseDbDto } from '@ellf/common-dto';
import { FormGroupState } from 'ngrx-forms';
import { NgxPermissionsService } from 'ngx-permissions';
import {
  combineLatest,
  firstValueFrom,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { BaseContainer } from './base-container';

@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export abstract class BaseFormContainer<
  FormT extends FormValueInterface,
  T extends BaseDbDto,
> extends BaseContainer {
  abstract sectionTitle: string;
  entity$ = this.facade.selectedEntity$;
  isExternalEntity$ = this.entity$.pipe(map((e) => !!e?.siaExternalId)); // TODO: - sistemare quando cambio SIA -> EUSIS
  isExternalGSEntity$ = this.entity$.pipe(map((e) => !!e?.gsLocked)); // TODO: - rivedere utlizzo di gsLocked
  formState$ = this.facade.selectForm$;
  isNew$ = this.facade.selectEntityId$.pipe(map((id) => id === '0'));
  forceCanDeactivate = false;
  createPermissions: string[] = [];
  updatePermissions: string[] = [];
  deletePermissions: string[] = [];

  @HostListener('window:beforeunload', ['$event'])
  public onBeforeUnload(event: any): void {
    this.isNavigationAllowed().then((isNavigationAllowed: boolean) => {
      if (event && !isNavigationAllowed) {
        event.preventDefault();
        event.returnValue = false;
      }
    });
  }

  private async isNavigationAllowed(): Promise<boolean> {
    const formState = await firstValueFrom(this.formState$);
    if (formState.isPristine || (formState.isSubmitted && formState.isValid)) {
      return true;
    } else {
      return false;
    }
  }

  canSwitchToEditMode$ = this.isNew$.pipe(
    switchMap((isNew) =>
      isNew
        ? of(false)
        : this.permissionsService.hasPermission(this.updatePermissions),
    ),
    map((hasPermissions) => {
      if (!this.routerService.isViewOnlyMode()) return false;

      return hasPermissions;
    }),
  );

  savePermissions$ = this.isNew$.pipe(
    map((isNew) => {
      return isNew ? this.createPermissions : this.updatePermissions;
    }),
  );

  canDelete$ = combineLatest([this.isExternalEntity$, this.isNew$]).pipe(
    switchMap(([isExternalEntity, isNew]) =>
      isNew || isExternalEntity
        ? of(false)
        : this.permissionsService.hasPermission(this.deletePermissions),
    ),
    map((hasPermissions) => {
      if (this.routerService.isAddMode() || this.routerService.isViewOnlyMode())
        return false;

      return hasPermissions;
    }),
  );

  canSave$ = this.isNew$.pipe(
    switchMap((isNew) =>
      isNew
        ? this.permissionsService.hasPermission(this.createPermissions)
        : this.permissionsService.hasPermission(this.updatePermissions),
    ),
    map((hasPermissions) => {
      if (this.routerService.isViewOnlyMode()) return false;

      return hasPermissions;
    }),
  );

  canNavigate$ = of(
    !this.routerService.isViewOnlyMode() && !this.routerService.isAddMode(),
  );

  constructor(
    protected routerService: RouterService,
    @Inject('FormFacade') protected facade: FacadeInterface<T>,
    protected permissionsService: NgxPermissionsService,
    private location: Location,
  ) {
    super();
  }

  switchToEditMode() {
    this.routerService.router.navigate([], {
      relativeTo: this.routerService.activatedRoute,
      queryParams: {
        [this.routerService.EDIT_MODE_PARAM]:
          this.routerService.activatedRoute.snapshot.queryParams[
            this.routerService.VIEW_MODE_PARAM
          ],
      },
      replaceUrl: true,
    });

    setTimeout(() => document.location.reload(), 400);
  }

  navigateForward(commands: any[]) {
    this.forceCanDeactivate = true;
    this.routerService.router.navigate(commands);
  }

  canDeactivate(): Observable<boolean> {
    return of(true);
  }

  canAddEntity(entityName: string) {
    return !!(
      this.permissionsService.getPermission(
        `${PERMISSION_CREATE}_${entityName}`,
      ) || this.permissionsService.getPermission(`${PERMISSION_CREATE}_all`)
    );
  }

  canViewEntity(entityName: string) {
    return !!(
      this.permissionsService.getPermission(
        `${PERMISSION_READ}_${entityName}`,
      ) || this.permissionsService.getPermission(`${PERMISSION_READ}_all`)
    );
  }

  formSaveCallback(item: T) {}

  save(form: FormGroupState<FormT>) {
    if (form.isValid) {
      this.facade.formSave(form, (item: T) => this.formSaveCallback(item));
    } else {
      this.facade.displayFormError();
    }
  }
  delete() {
    if (confirm('Confermi di voler eliminare questo elemento?')) {
      this.facade.formDelete();
    }
  }

  goBack() {
    this.location.back();
  }
}
